<template>
  <Modal v-if="show" :show="show" :close="close" max-width="1200" :show-dividers="false">
    <template #title>
      <v-row class="justify-end" no-gutters>
        <v-icon @click="close">mdi-close</v-icon>
      </v-row>
    </template>
    <template #text>
      <v-tabs v-model="currentTab" vertical>
        <v-tab v-for="(tab, index) in tabs" :key="`v-tab-header-${index}`" class="justify-start" :class="{'third--text': currentTab === index}">
          <v-icon left>{{ tab.icon }}</v-icon>
          <span>{{ tab.label }}</span>
        </v-tab>

        <v-tabs-slider color="red" />

        <v-tabs-items v-model="currentTab">
          <v-tab-item v-for="(tab, index) in tabs" :key="`v-tab-item-${index}`">
            <div v-if="loadingOrders" class="d-flex justify-center align-center" style="min-height: 500px;">
              <v-progress-circular indeterminate color="third" size="64" />
            </div>
            <component
              v-else-if="index === currentTab"
              :is="tab.component"
              :customer="customer"
              :contract="contract"
              :initial-stock="initialStock"
              :contract-history="contractHistory"
              @stock-updated="handleStockUpdated"
              @history-changed="loadContractHistory"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </template>
  </Modal>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api';
import Modal from '@/components/common/Modal.vue';
import UpdateStock from './UpdateStock.vue';
import UpdateStockHistory from './UpdateStockHistory.vue';
import ContractSessionHistory from './ContractSessionHistory.vue';
import { sendNotification } from '@/helpers/utils';
import { getOrders, getContractStockHistory } from '@/helpers/request';

export default {
  components: { Modal, UpdateStock, UpdateStockHistory, ContractSessionHistory },
  props: {
    show: { type: Boolean, default: false },
    order: { type: Object, default: null },
    customer: { type: Object, default: null },
    fetchedOrders: { type: Array, default: () => [] },
    onClose: { type: Function, default: null },
    onStockUpdated: { type: Function, default: null }
  },
  setup (props) {
    const currentTab = ref(0);
    const loadingOrders = ref(false);
    const allContractOrders = ref([]);
    const contractHistory = ref([]);

    const tabs = [
      { icon: 'mdi-plus-circle-outline', label: 'Mettre à jour le stock', component: 'UpdateStock' },
      { icon: 'mdi-note-edit-outline', label: 'Historique des Modifications', component: 'UpdateStockHistory' },
      { icon: 'mdi-note-edit-outline', label: 'Historique des sessions', component: 'ContractSessionHistory' }
    ];

    const contract = computed(() => {
      if (!props.order || !props.customer) return null;
      const orderItem = props.order.order.order_items.find(item => item.customer.uuid === props.customer.uuid);
      if (!orderItem) return null;
      return orderItem.contract ? { ...orderItem.contract, workspace: props.order.siteWorkspaceCode } : null;
    });

    const initialStock = computed(() => {
      if (!contract.value) return 0;
      if (!contract.value.orderUuids || contract.value.orderUuids.length <= 1) {
        return parseInt(contract.value.initialStock) || 0;
      }
      let totalStock = 0;
      if (allContractOrders.value && allContractOrders.value.length) {
        allContractOrders.value.forEach(order => {
          order.order.order_items.forEach(item => {
            if (item.customer.uuid === props.customer.uuid) {
              totalStock = totalStock + (parseInt(item.stock) * parseInt(item.quantity));
            }
          });
        });
      }
      return totalStock;
    });

    const loadAllContractOrders = async () => {
      if (!contract.value || !contract.value.orderUuids) {
        allContractOrders.value = [];
        return;
      }
      const ordersFromProps = [];
      const ordersToFetch = [];

      contract.value.orderUuids.forEach(orderUuid => {
        const order = props.fetchedOrders.find(o => o.uuid === orderUuid);
        if (order) {
          ordersFromProps.push(order);
        } else {
          ordersToFetch.push(orderUuid);
        }
      });

      if (ordersToFetch.length) {
        const response = await getOrders(0, ordersToFetch.length, props.customer.uuid, 'alpha', undefined);
        if (response.success && response.data && response.data.items) {
          ordersFromProps.push(...response.data.items.filter(item => ordersToFetch.includes(item.uuid)));
        }
      }
      allContractOrders.value = ordersFromProps;
    };

    const loadContractHistory = async () => {
      if (!contract.value || !contract.value.contractNumber) {
        contractHistory.value = [];
        return;
      }
      const response = await getContractStockHistory(props.customer.uuid, contract.value.contractNumber);
      if (response.success) {
        contractHistory.value = response.data || [];
      } else {
        contractHistory.value = [];
      }
    };

    const loadContractData = async () => {
      if (!contract.value) return;
      loadingOrders.value = true;
      try {
        await Promise.all([loadAllContractOrders(), loadContractHistory()]);
      } catch (error) {
        console.error('Error loading contract data:', error);
        sendNotification('error', 'Erreur lors de la récupération des données du contrat');
      } finally {
        loadingOrders.value = false;
      }
    };

    const close = () => {
      currentTab.value = 0;
      if (props.onClose) props.onClose();
    };

    const handleStockUpdated = (data) => {
      if (props.onStockUpdated) props.onStockUpdated(data);
    };

    watch(() => props.show, (val) => {
      if (val) loadContractData();
    });

    return { currentTab, tabs, contract, initialStock, loadingOrders, contractHistory, close, handleStockUpdated, loadContractHistory };
  }
};
</script>
